/**
 * @file src/offline/collectionManifest.js
 * @description Turn a collection into the list of files that make it playable with no server.
 *
 * A collection's roster names entities; it does not name files. Files are named by RELEASES, so the
 * manifest is built from a full hydration of each roster entry — the same `hydrate=2` payload a
 * voice boots from, which carries the track's audio assets AND the orbiter and world releases it
 * plays with. Fetching anything less would leave a downloaded collection able to name its tracks
 * and unable to draw them.
 *
 * ## Versions are pinned here, once
 *
 * Every entry records the exact `trackVersion` / `orbiterVersion` / `entangledWorldVersion` it was
 * built from. That is not bookkeeping — an unpinned request resolves `version: null`, seeds the
 * release cache under the live key and would boot a downloaded collection against a release whose
 * bytes were never fetched. The pin is what makes the offline boot ask for the thing that is
 * actually here.
 *
 * ## What is stored, and what is deliberately not
 *
 * Audio is the COMPRESSED streaming variant only (see `offlineConfig.js`). The waveform and the
 * cover come along because they are small and their absence is visible. World textures are resolved
 * per world — two or three files — rather than caching the whole platform catalog. The skybox is
 * recorded as a catalog id and its six faces resolved from it — before the skybox moved into the
 * texture catalog there was a hard-coded URL and nothing to record.
 */
import { fetchTrackRelease } from '../api/dataManager/loaders.js';
import {
  normalizeTrackRelease,
  normalizeWorldRelease,
  pickHydratedRelease,
} from '../api/dataManager/normalizers.js';
import { resolveWorldTextureUrls } from '../world/worldTextureUrls.js';
import { resolveTrackCoverUrl } from './offlineAssetBindings.js';
import { getDefaultSkybox } from 'entangled-worlds-orbiters-shared/config';
import { faceOfUrl, skyboxFaceUrls } from '../world/skyboxFaceUrls.js';
import { platformTextureCatalogUrl, readPlatformTextureCatalog } from '../api/textureCatalog.js';
import { fetchEntangledWorldRelease, fetchOrbiterRelease } from '../api/dataManager/loaders.js';
import { resolveApiBase } from '../api/httpClient.js';
import { RELEASE_KIND, releaseRecordKey } from './offlineLibraryStore.js';
import {
  DEFAULT_OFFLINE_QUALITY,
  audioVariantForQuality,
  normalizeOfflineQuality,
  OFFLINE_QUALITY,
  trackHasQuality,
} from './offlineConfig.js';
import { buildOfflineEntryUrl } from './offlineEntryUrl.js';
import {
  catalogueKey,
  collectionCoverKey,
  coverKey,
  entityCoverKey,
  skyboxFaceKey,
  textureKey,
  toCacheRequestUrl,
  trackAudioKey,
  waveformKey,
} from './offlineKeys.js';

function normalize(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/**
 * The URL a downloaded track's bytes are fetched from — the `/tracks/:id/audio` proxy, always.
 *
 * Never the presigned `originalUrl` a release may also carry: that URL expires, and a download is a
 * thing that has to still work tomorrow. The proxy is stable, is keyed by version and variant, and
 * re-authorizes on every request, so a resumed download a week later asks the same question and
 * gets the same answer about whether this person may still have these bytes.
 */
function buildDownloadAudioUrl({ trackId, version, variant }) {
  const base = resolveApiBase();
  const numericVersion = Number.parseInt(version, 10);
  // The proxy serves published releases only; a draft (version 0) has no stable URL to pin, so it
  // is simply not downloadable rather than downloadable from a URL that will stop working.
  if (!base || !trackId || !Number.isInteger(numericVersion) || numericVersion < 1) return null;
  try {
    const url = new URL(`${String(base).replace(/\/+$/, '')}/tracks/${encodeURIComponent(trackId)}/audio`);
    url.searchParams.set('v', String(numericVersion));
    url.searchParams.set('variant', variant);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * One asset: what it is called in the store, and where its bytes come from when they are not there.
 * Both halves are required — a key with no source can never be repaired after an eviction, and a
 * source with no key cannot be found again.
 */
function asset(key, url, kind) {
  const identity = normalize(key);
  const source = normalize(url);
  if (!identity || !source) return null;
  const requestUrl = toCacheRequestUrl(identity);
  return requestUrl ? { key: requestUrl, identity, url: source, kind } : null;
}

/**
 * Mark an asset as one whose absence must not make the download incomplete.
 *
 * A missing asset normally means `partial`, which is right for the things a collection is: its audio,
 * its textures. It is wrong for the decoration around them — a sky that the platform ships in one
 * detail level instead of two would otherwise turn every download of every collection partial, and
 * every resume would fail the same files again forever.
 */
function optional(entry) {
  return entry ? { ...entry, optional: true } : null;
}

/**
 * The sky, and the document that says which sky it is.
 *
 * Both were missing from every download, for different reasons. The catalogue was simply never
 * stored, so offline the shared loader fell back to a copy hard-coded in the package — free to name a
 * different default than the platform serves. The FACES were never stored either: the code that
 * pinned them took the skybox as an argument and no caller ever passed one, so it sat unreachable.
 *
 * One sky per download, not one per track — it is the platform's default, the same for every entry —
 * so these are COLLECTION-level assets, beside the cover.
 *
 * The catalogue says WHICH sky; `skyboxFaceUrls` says where the renderer will look for it. Those are
 * two different questions and reading the second out of the catalogue was the first attempt's bug —
 * see that module for why it mirrors the loader instead.
 *
 * The faces are OPTIONAL assets. Twelve files that must all land would mean that the day the
 * platform's default sky ships without a low-detail set, or as PNG only, every download of every
 * collection turns `partial` and every resume fails the same six faces again. A collection with no
 * sky is a collection, and that is what the rest of this function already promises.
 */

/**
 * The catalogue and the default sky's faces, as collection-level assets.
 *
 * The catalogue is read through `readPlatformTextureCatalog`, which has NO fallback, and that matters:
 * the loader the renderer uses answers a failed fetch with a copy hard-coded in the shared package,
 * and that copy is free to name a different default than the platform serves. Storing the sky it named
 * would pin six faces the renderer never asks for and then call the sky downloaded. No catalogue
 * therefore means no sky stored, which is what every download before this had — never a guess.
 *
 * Never blocks a download: a collection with no sky still plays.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ assets: object[], skyboxId: string|null }>}
 */
async function skyAssets({ signal } = {}) {
  const catalogueUrl = platformTextureCatalogUrl();
  if (!catalogueUrl) return { assets: [], skyboxId: null };

  const catalogue = await readPlatformTextureCatalog({ signal });
  if (!catalogue) {
    console.warn('[offline] No texture catalogue for this download; its sky will not be stored.');
    return { assets: [], skyboxId: null };
  }

  const assets = [];
  const catalogueAsset = optional(asset(catalogueKey(catalogueUrl), catalogueUrl, 'catalogue'));
  if (catalogueAsset) assets.push(catalogueAsset);

  const { id, urls } = skyboxFaceUrls(await getDefaultSkybox(catalogue));
  for (const url of urls) {
    const face = faceOfUrl(url);
    const entry = face ? asset(skyboxFaceKey({ skyboxId: id, face, url }), url, 'skybox') : null;
    if (entry) assets.push(optional(entry));
  }
  return { assets, skyboxId: id };
}

/**
 * The account's default orbiter and world, as something a download keeps.
 *
 * Not decoration and not this collection's business, which is exactly why it was missed: every
 * account HAS a default pair, it is what any fallback resolves to, and it is the whole answer for a
 * file imported from this machine — a local file has no author and no track document, so nobody ever
 * paired it. With no connection the chain that finds it cannot run: the settings read fails, the
 * platform session cannot be hydrated, and the in-app fallback constants are reached and then
 * requested from the API by id, which fails. The world does not draw.
 *
 * So the pair is resolved here, while there IS a connection, and all three parts of it are stored:
 * the ids and versions on the manifest, the two release payloads as release records, and the world's
 * textures as assets. `offlineDefaultPair.js` puts them back.
 *
 * The textures are required download assets. Runtime can still degrade to a plain world if a stored
 * texture is later reclaimed or corrupt, but the downloader must not call the collection complete
 * while it already knows the fallback world is incomplete.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ pair: object|null, assets: object[], releaseRecords: object[] }>}
 */
export async function resolveDefaultPairManifestDependency({ signal } = {}) {
  const empty = { pair: null, assets: [], releaseRecords: [], unresolved: true };
  // Checked before settings resolution; the release lookups below also receive the signal.
  if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');

  let pair;
  try {
    // Dynamic, and not as a style choice: the pair reaches account settings, which reaches viewer
    // auth, which is session auth — and session auth imports the offline library, so a STATIC import
    // here closes a cycle whose victim is whichever half the bundler loads second. This is a download
    // path, so the extra round trip costs nothing. `tests/offline/offlineImportCycle.test.js` guards it.
    const { resolveDefaultPair } = await import('../config/defaultPair.js');
    pair = await resolveDefaultPair();
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.warn('[offline] Could not resolve the fallback orbiter and world for this download.', error);
    return empty;
  }

  const orbiterId = normalize(pair?.orbiterId);
  const worldId = normalize(pair?.worldId);
  if (!orbiterId || !worldId) return empty;

  // `resolveDefaultPair` normally carries both releases. Keep the fetches as a narrow compatibility
  // boundary for an older/mocked resolver that still returns only ids.
  let orbiterPayload = pair.orbiterRelease ? { release: pair.orbiterRelease } : null;
  let worldPayload = pair.worldRelease ? { release: pair.worldRelease } : null;
  try {
    if (!orbiterPayload) {
      const fetched = await fetchOrbiterRelease(orbiterId, {
        version: pair.orbiterVersion ?? null,
        useFallback: false,
        signal,
      });
      orbiterPayload = fetched?.release ? fetched : null;
    }
    if (!worldPayload) {
      const fetched = await fetchEntangledWorldRelease(worldId, {
        version: pair.worldVersion ?? null,
        signal,
      });
      worldPayload = fetched?.release ? fetched : null;
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.warn("[offline] Could not read the fallback pair's releases for this download.", error);
  }

  if (!orbiterPayload || !worldPayload) {
    console.warn('[offline] This download has no stored fallback world; a local file played offline will draw a plain one.');
    return empty;
  }
  if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');

  const orbiterVersion = orbiterPayload.release?.version ?? pair.orbiterVersion ?? null;
  const worldVersion = worldPayload.release?.version ?? pair.worldVersion ?? null;

  // The world's own texture files, resolved exactly as a collection entry's are.
  const world = normalizeWorldRelease({ worldId, release: worldPayload.release });
  const assets = [];
  for (const url of resolveWorldTextureUrls(world)) {
    const entry = asset(textureKey({ worldId, version: worldVersion, url }), url, 'texture');
    if (entry) assets.push(entry);
  }

  return {
    pair: {
      orbiterId,
      orbiterVersion,
      worldId,
      worldVersion,
      resolvedAt: Number(pair?.resolvedAt) || Date.now(),
    },
    assets,
    releaseRecords: [
      { key: releaseRecordKey({ kind: RELEASE_KIND.ORBITER, id: orbiterId, version: orbiterVersion }), payload: orbiterPayload },
      { key: releaseRecordKey({ kind: RELEASE_KIND.WORLD, id: worldId, version: worldVersion }), payload: worldPayload },
    ].filter((record) => record.key),
    unresolved: false,
  };
}

/**
 * Which quality this track will actually be stored at, given the one that was asked for.
 *
 * A collection is asked for at ONE quality; a track either has that variant or it does not. Taking
 * the other one is better than leaving a hole: a downloaded collection that silently omits two of
 * its forty tracks is a worse promise than one that says two of them are compressed. Both flags are
 * known before a byte is fetched, so a quote can include the substitution rather than discover it.
 *
 * Returns null when the track has NEITHER variant — a draft, or a card that is not a recording.
 */
function resolveEntryQuality(track, requested) {
  const asked = normalizeOfflineQuality(requested);
  if (trackHasQuality(track, asked)) return asked;
  const other =
    asked === OFFLINE_QUALITY.LOSSLESS ? OFFLINE_QUALITY.COMPRESSED : OFFLINE_QUALITY.LOSSLESS;
  return trackHasQuality(track, other) ? other : null;
}

/**
 * Build one roster entry's manifest entry from its hydrated release.
 *
 * Returns null for an entry with no downloadable audio at all. That covers a draft and an orbiter or
 * world card — which are not recordings and have nothing to play. A collection of those downloads as
 * an empty manifest rather than a broken one.
 *
 * ## The variant is recorded PER ENTRY, not per collection
 *
 * The collection records what was ASKED for; each entry records what it actually holds. They differ
 * whenever a track lacks the asked-for variant, and — more importantly — all through a quality
 * switch, which replaces one entry's audio at a time. A manifest that named the target variant for
 * forty tracks while twelve had arrived would report the other twenty-eight as missing, and an
 * offline boot would refuse a collection whose compressed copies are sitting right there. So the
 * entry names the bytes that exist, always, and "what quality is this collection" is a question
 * answered by reading the entries.
 */
export function buildManifestEntry(
  rosterEntry,
  hydratedPayload,
  { quality = DEFAULT_OFFLINE_QUALITY, unresolvedDependencies = [] } = {},
) {
  const trackId = normalize(rosterEntry?.trackId);
  if (!trackId || !hydratedPayload) return null;

  const track = normalizeTrackRelease(hydratedPayload);
  const trackVersion = track?.version ?? null;
  if (trackVersion === null || trackVersion === undefined) return null;

  // The variant flags, not the URLs: a listener's payload carries neither key nor signed URL, and
  // reading absence from the URL would make every listener's collection undownloadable.
  const entryQuality = resolveEntryQuality(track, quality);
  if (!entryQuality) return null;
  const { variant, assetFormat } = audioVariantForQuality(entryQuality);

  const audioUrl = buildDownloadAudioUrl({
    trackId,
    version: trackVersion,
    variant,
  });
  if (!audioUrl) return null;

  const audioAsset = asset(
    trackAudioKey({
      trackId,
      version: trackVersion,
      variant,
      assetFormat,
    }),
    audioUrl,
    'audio',
  );
  if (!audioAsset) return null;

  const hydration = hydratedPayload?.hydration || null;
  const orbiterRelease = pickHydratedRelease(
    hydration?.orbiterRelease,
    hydration?.orbiterFallbackRelease,
  );
  const worldReleaseRaw = pickHydratedRelease(
    hydration?.worldRelease,
    hydration?.worldFallbackRelease,
  );
  const usesOrbiterFallback = orbiterRelease && orbiterRelease === hydration?.orbiterFallbackRelease;
  const usesWorldFallback = worldReleaseRaw && worldReleaseRaw === hydration?.worldFallbackRelease;
  const orbiterId =
    normalize(usesOrbiterFallback ? hydration?.orbiterFallbackId : hydration?.orbiterId)
    || normalize(usesOrbiterFallback ? hydration?.orbiterId : hydration?.orbiterFallbackId)
    || normalize(orbiterRelease?.metadata?.orbiterId)
    || normalize(orbiterRelease?.metadata?.id)
    || normalize(track.defaultOrbiterId)
    || normalize(rosterEntry?.orbiterId);
  const worldId =
    normalize(usesWorldFallback ? hydration?.worldFallbackId : hydration?.worldId)
    || normalize(usesWorldFallback ? hydration?.worldId : hydration?.worldFallbackId)
    || normalize(worldReleaseRaw?.metadata?.permissions?.entityId)
    || normalize(worldReleaseRaw?.metadata?.worldId)
    || normalize(worldReleaseRaw?.metadata?.id)
    || normalize(track.defaultEntangledWorldId)
    || normalize(rosterEntry?.entangledWorldId);
  const orbiterVersion = orbiterRelease?.version ?? hydration?.orbiterVersion ?? track.defaultOrbiterVersion ?? null;

  // `normalizeWorldRelease` reads a release RESPONSE (`{ release: {...} }`); the hydration carries
  // the release itself, so it is wrapped rather than passed raw — otherwise every texture field
  // reads as absent and the world downloads with no textures at all.
  const world = worldReleaseRaw
    ? normalizeWorldRelease({ worldId, release: worldReleaseRaw })
    : null;
  const worldVersion = world?.version ?? hydration?.worldVersion ?? track.defaultEntangledWorldVersion ?? null;

  const assets = [audioAsset];

  const waveform = optional(asset(
    waveformKey({ trackId, version: trackVersion }),
    track.waveformJSONURL,
    'waveform',
  ));
  if (waveform) assets.push(waveform);

  const cover = optional(asset(
    coverKey({ trackId, version: trackVersion }),
    resolveTrackCoverUrl(track),
    'cover',
  ));
  if (cover) assets.push(cover);

  const textureUrls = resolveWorldTextureUrls(world);
  for (const url of textureUrls) {
    const entry = asset(textureKey({ worldId, version: worldVersion, url }), url, 'texture');
    if (entry) assets.push(entry);
  }

  const releaseKeys = [releaseRecordKey({ kind: RELEASE_KIND.TRACK, id: trackId, version: trackVersion })].filter(Boolean);

  return {
    voiceId: normalize(rosterEntry?.voiceId) || trackId,
    itemId: normalize(rosterEntry?.itemId),
    title: rosterEntry?.title ?? track.trackName ?? null,
    // The card's second line and its official mark. Display-only, and stored for the same reason
    // the title is: an offline boot builds its cards from THIS and has nothing else to ask.
    subtitle: rosterEntry?.subtitle ?? null,
    isOfficial: rosterEntry?.isOfficial === true,
    /**
     * What this stage IS. Written even though `track` is what an absent value means: a collection can
     * hold an orbiter or a world on its own, the boot hands each kind a different release, and an
     * untyped row leaves that to be inferred. Stored manifests from before this field read as tracks,
     * which is what they were.
     */
    entityType: 'track',
    trackId,
    trackVersion,
    orbiterId,
    orbiterVersion,
    entangledWorldId: worldId,
    entangledWorldVersion: worldVersion,
    // Human-readable identities used by the offline boot. The resolution table below carries the
    // versioned logical key for every URL.
    audio: [audioAsset.identity],
    // What this entry's audio actually IS. Read by playback to pick the stored bytes over the
    // streaming preset, by the library page to say what a collection holds, and by a switch to know
    // which entries are still to do.
    audioQuality: entryQuality,
    audioVariant: variant,
    audioFormat: assetFormat,
    waveform: waveform?.identity ?? null,
    cover: cover?.identity ?? null,
    textures: textureUrls,
    // The resolution table the download and every later repair read: key → where its bytes live.
    assets,
    releaseKeys,
    unresolvedDependencies,
  };
}

/**
 * A roster item that is an ORBITER or a WORLD, as a manifest entry.
 *
 * A collection is not only tracks. An orbiter on a stage is an instrument you play — it has no audio
 * file at all — and a world is a place to play it in. Both were dropped on the floor by a download,
 * which filtered the roster down to items with a `trackId`, so a mixed collection came back offline
 * with its tracks and empty stages where everything else had been.
 *
 * What such an entry needs is its OWN release and nothing else: an orbiter is fully described by its
 * release payload (the app never fetches the orbiter file the metadata names — the parameters and
 * effects are inline), and a world needs its release plus the texture files it points at. The other
 * half of the pair comes from the account's fallback, which a download stores anyway.
 *
 * `audio` is an empty list, deliberately and not as an omission: there is nothing to fetch and an
 * entry that claimed audio it has no key for would be judged unplayable forever.
 *
 * @param {object} rosterEntry
 * @param {{ orbiterId: string|null, orbiterVersion: any, worldId: string|null, worldVersion: any }|null} fallbackPair
 * @returns {Promise<{ entry: object, releaseRecords: object[] }|null>}
 */
async function buildEntityManifestEntry(rosterEntry, fallbackPair, signal) {
  const entityType = normalize(rosterEntry?.entityType)?.toLowerCase() ?? null;
  const isOrbiter = entityType === 'orbiter' || (!entityType && normalize(rosterEntry?.orbiterId));
  const isWorld =
    entityType === 'world'
    || entityType === 'entangled-world'
    || (!entityType && normalize(rosterEntry?.entangledWorldId));
  if (!isOrbiter && !isWorld) return null;

  const releaseRecords = [];
  const assets = [];
  let orbiterId = normalize(rosterEntry?.orbiterId);
  let worldId = normalize(rosterEntry?.entangledWorldId);
  let orbiterVersion = null;
  let worldVersion = null;

  if (isOrbiter) {
    if (!orbiterId) return null;
    // `useFallback` left ON so a release that is simply NOT THERE comes back as a descriptor rather
    // than a throw. The difference decides where the item is filed: an entity with no published
    // release is permanently un-downloadable (`skipped`), while a request that failed for any other
    // reason is worth retrying (`failed`). Filed as a failure, a collection holding one such item
    // would read `partial` forever and every resume would fail it again.
    const payload = await fetchOrbiterRelease(orbiterId, { version: null, signal });
    if (!payload) throw new Error('The orbiter release lookup did not answer.');
    if (payload?.error) {
      if (payload.isNotFound || isUnpublishedReleaseError(payload.error)) return null;
      throw payload.error;
    }
    if (!payload.release) return null;
    orbiterVersion = payload.release.version ?? null;
    const key = releaseRecordKey({ kind: RELEASE_KIND.ORBITER, id: orbiterId, version: orbiterVersion });
    if (key) releaseRecords.push({ key, payload });
    // The world it plays in is the account's fallback, which is stored for the whole download.
    worldId = worldId || fallbackPair?.worldId || null;
    worldVersion = fallbackPair?.worldVersion ?? null;
  }

  if (isWorld) {
    if (!worldId) return null;
    // The world loader has no fallback flag and throws, so the same distinction is drawn from the
    // error: `*-not-found` is permanent, anything else is worth retrying. See the orbiter above.
    let payload;
    try {
      payload = await fetchEntangledWorldRelease(worldId, { version: null, signal });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      if (String(error?.message || '').includes('not-found') || isUnpublishedReleaseError(error)) return null;
      throw error;
    }
    if (!payload) throw new Error('The world release lookup did not answer.');
    if (!payload.release) return null;
    worldVersion = payload.release.version ?? null;
    const key = releaseRecordKey({ kind: RELEASE_KIND.WORLD, id: worldId, version: worldVersion });
    if (key) releaseRecords.push({ key, payload });
    const world = normalizeWorldRelease({ worldId, release: payload.release });
    for (const url of resolveWorldTextureUrls(world)) {
      const textureAsset = asset(textureKey({ worldId, version: worldVersion, url }), url, 'texture');
      if (textureAsset) assets.push(textureAsset);
    }
    // A world row must remain a world row. The collection app and stage swap both classify by id
    // precedence, so attaching a fallback orbiter here makes them boot/swap the orbiter instead.
    orbiterId = null;
    orbiterVersion = null;
  }

  // The drawer already resolved the card's public thumbnail while online. Keep those bytes beside
  // the entity release just as a track entry keeps its cover; otherwise the offline roster has the
  // right title and type but can only render its letter placeholder. The key names the entity and
  // release, so two releases that happen to reuse a URL do not claim to be the same stored cover.
  const cover = optional(asset(
    entityCoverKey({
      entityType: isOrbiter ? 'orbiter' : 'world',
      entityId: isOrbiter ? orbiterId : worldId,
      version: isOrbiter ? orbiterVersion : worldVersion,
    }),
    rosterEntry?.image,
    'cover',
  ));
  if (cover) assets.push(cover);

  return {
    entry: {
      voiceId: normalize(rosterEntry?.voiceId) || orbiterId || worldId,
      itemId: normalize(rosterEntry?.itemId),
      title: rosterEntry?.title ?? null,
      subtitle: rosterEntry?.subtitle ?? null,
      isOfficial: rosterEntry?.isOfficial === true,
      /** What this stage IS. Read by the offline boot to know which release to hand the voice. */
      entityType: isOrbiter ? 'orbiter' : 'world',
      trackId: null,
      trackVersion: null,
      orbiterId,
      orbiterVersion,
      entangledWorldId: worldId,
      entangledWorldVersion: worldVersion,
      // Nothing to fetch, and nothing to judge as missing. See the note above.
      audio: [],
      audioQuality: null,
      audioVariant: null,
      audioFormat: null,
      waveform: null,
      cover: cover?.identity ?? null,
      textures: assets.filter((item) => item.kind === 'texture').map((item) => item.url),
      assets,
      releaseKeys: releaseRecords.map((record) => record.key),
    },
    releaseRecords,
  };
}

/** A valid entity with no published release cannot become downloadable by retrying this release. */
function isUnpublishedReleaseError(error) {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'noreleaseyet'
    || message.includes('no release is available yet')
    || message.includes('noreleaseyet');
}

/**
 * Collection hydration normally carries the world release inline. A few older/partial responses
 * carry only the world id, though; the live assembler follows that id with a second release read.
 * A download has to do the same before it can discover the texture URLs, otherwise the track looks
 * complete while its world (often just the shared cloud layer) is absent from the manifest.
 */
async function ensureDependencyReleasesForManifest(payload, signal) {
  const hydration = payload?.hydration || {};
  const track = normalizeTrackRelease(payload);
  if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');
  const next = { ...hydration };
  const unresolvedDependencies = [];

  if (!pickHydratedRelease(hydration.orbiterRelease, hydration.orbiterFallbackRelease)) {
    const orbiterId = normalize(
      hydration.orbiterId || hydration.orbiterFallbackId || track?.defaultOrbiterId,
    );
    if (orbiterId) {
      const version = hydration.orbiterVersion ?? track?.defaultOrbiterVersion ?? null;
      try {
        const orbiterPayload = await fetchOrbiterRelease(orbiterId, {
          version,
          useFallback: false,
          signal,
        });
        if (!orbiterPayload?.release) throw new Error('The track orbiter release lookup did not answer.');
        next.orbiterId = orbiterId;
        next.orbiterRelease = orbiterPayload.release;
        next.orbiterVersion = orbiterPayload.release.version ?? version;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        unresolvedDependencies.push({ kind: 'orbiter', id: orbiterId, version });
      }
    }
  }

  if (!pickHydratedRelease(hydration.worldRelease, hydration.worldFallbackRelease)) {
    const worldId = normalize(
      hydration.worldId || hydration.worldFallbackId || track?.defaultEntangledWorldId,
    );
    if (worldId) {
      const version = hydration.worldVersion ?? track?.defaultEntangledWorldVersion ?? null;
      try {
        const worldPayload = await fetchEntangledWorldRelease(worldId, { version, signal });
        if (!worldPayload?.release) throw new Error('The track world release lookup did not answer.');
        next.worldId = worldId;
        next.worldRelease = worldPayload.release;
        next.worldVersion = worldPayload.release.version ?? version;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        unresolvedDependencies.push({ kind: 'world', id: worldId, version });
      }
    }
  }

  return { payload: { ...payload, hydration: next }, unresolvedDependencies };
}

/** Re-resolve one stored track's visual dependencies without changing its pinned audio release. */
export async function repairTrackManifestEntry(entry, payload, { signal } = {}) {
  const resolved = await ensureDependencyReleasesForManifest(payload, signal);
  const rebuilt = buildManifestEntry(entry, resolved.payload, {
    quality: entry?.audioQuality ?? DEFAULT_OFFLINE_QUALITY,
    unresolvedDependencies: resolved.unresolvedDependencies,
  });
  return rebuilt
    ? {
      entry: { ...rebuilt, voiceId: entry.voiceId, itemId: entry.itemId },
      payload: resolved.payload,
    }
    : null;
}

/**
 * Build the whole manifest for a collection.
 *
 * Entries are hydrated one at a time on purpose. Mycelium's per-item hydration is measured in
 * seconds and a collection can hold dozens; firing them all at once buys nothing (the server is the
 * bottleneck, not the client) and makes the progress report a lie, since nothing can be said to be
 * done until everything is. `onProgress` reports resolution separately from the byte download that
 * follows, because on a slow backend the resolve is the part that looks like a hang.
 *
 * A single entry that will not hydrate is skipped, not fatal: one archived track in a collection of
 * twenty should cost that track, not the download.
 *
 * @param {{ collectionId: string, name?: string|null, coverImage?: string|null, layout?: object|null, roster: object[] }} collection
 * @param {{ signal?: AbortSignal, onProgress?: (done: number, total: number) => void, sourceUrl?: string|null }} [options]
 * @returns {Promise<object>} the manifest, ready for `offlineLibraryStore`
 */
export async function buildCollectionManifest(
  collection,
  {
    signal,
    onProgress = null,
    sourceUrl = null,
    quality = DEFAULT_OFFLINE_QUALITY,
  } = {},
) {
  const requestedQuality = normalizeOfflineQuality(quality);
  const roster = Array.isArray(collection?.roster) ? collection.roster : [];
  // Every item, not only the ones with a track behind them. A collection can hold an orbiter on its
  // own — an instrument, with no audio file anywhere — and a world on its own, and filtering those
  // out is why a mixed collection came back offline with empty stages where they had been.
  const downloadable = roster.filter(
    (entry) => normalize(entry?.trackId) || normalize(entry?.orbiterId) || normalize(entry?.entangledWorldId),
  );
  const entries = [];
  const releaseRecords = [];
  const skipped = [];
  const failed = [];
  const failedEntries = [];

  // The sky: the catalogue that names it, and the faces it names. Resolved once for the whole
  // download — see `skyAssets` for why it is not per entry and why both detail levels are kept.
  const sky = await skyAssets({ signal });

  // The account's fallback orbiter and world. Resolved BEFORE the entries, because an item that is an
  // orbiter on its own borrows a world from it, and an item that is a world borrows an orbiter.
  const defaults = await resolveDefaultPairManifestDependency({ signal });

  let done = 0;
  for (const rosterEntry of downloadable) {
    if (signal?.aborted) throw new DOMException('Download cancelled', 'AbortError');
    try {
      // The signal goes INTO the request, not just around it. Checking `aborted` between items
      // left a cancel waiting out whichever hydration was in flight — and mycelium's are measured
      // in seconds — so a removal waiting on that cancel stalled with it.
      if (normalize(rosterEntry.trackId)) {
        const fetchedPayload = await fetchTrackRelease(rosterEntry.trackId, { hydrate: 2, signal });
        const resolved = await ensureDependencyReleasesForManifest(fetchedPayload, signal);
        const payload = resolved.payload;
        const entry = buildManifestEntry(rosterEntry, payload, {
          quality: requestedQuality,
          unresolvedDependencies: resolved.unresolvedDependencies,
        });
        if (entry) {
          entries.push(entry);
          releaseRecords.push({
            key: releaseRecordKey({ kind: RELEASE_KIND.TRACK, id: entry.trackId, version: entry.trackVersion }),
            payload,
          });
        } else {
          // Nothing downloadable about it: a draft, or a card that is not a recording. Permanent.
          skipped.push(rosterEntry.trackId);
        }
      } else {
        // An orbiter or a world on its own: no audio anywhere, just its own release and, for a world,
        // the textures it points at. See `buildEntityManifestEntry`.
        const built = await buildEntityManifestEntry(rosterEntry, defaults.pair, signal);
        if (built) {
          entries.push(built.entry);
          releaseRecords.push(...built.releaseRecords);
        } else {
          skipped.push(normalize(rosterEntry.orbiterId) || normalize(rosterEntry.entangledWorldId));
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      console.warn(
        '[offline] Could not resolve a collection entry for download.',
        rosterEntry?.trackId || rosterEntry?.orbiterId || rosterEntry?.entangledWorldId,
        error,
      );
      // A request that FAILED is a different fact and must not be filed with the permanent ones:
      // treated as un-downloadable, one timed-out hydration would exclude that track from the roster
      // check for good, leaving the collection calling itself complete with no route to ever fetch
      // it.
      failed.push(
        normalize(rosterEntry.trackId)
          || normalize(rosterEntry.orbiterId)
          || normalize(rosterEntry.entangledWorldId),
      );
      failedEntries.push({ ...rosterEntry });
    }
    done += 1;
    onProgress?.(done, downloadable.length);
  }

  // The collection's OWN artwork. It used to be recorded as a URL and nothing else, which read as
  // "we have the cover" and meant "we have a link to the cover" — so every card on the library page
  // drew a broken image the moment there was no connection, which is the only condition that page
  // exists for. A picture that is only a URL is not downloaded.
  const collectionCover = collectionCoverAsset(collection?.coverImage);

  return {
    collectionId: collection.collectionId,
    // What KIND of thing was downloaded. A collection today, and only a collection — but the
    // library page renders one card per downloaded entity and reads this to know what it is
    // looking at, so a track or a world joining later is a new value here rather than a second
    // page. Writing it now costs a field; retrofitting it means every stored entry is untyped.
    kind: 'collection',
    // The quality this download was ASKED for. Descriptive, never authoritative: what plays and what
    // is released on removal is decided by the entries and by `pinnedKeys`, which are the record of
    // bytes that actually exist. A collection asked for as lossless can hold compressed entries
    // (a track with no lossless variant, or a switch still in flight), and reading this field as if
    // it named the keys would unpin audio that is still being played.
    requestedQuality,
    name: collection?.name ?? null,
    coverImage: collection?.coverImage ?? null,
    // The saved arrangement, kept with the download. Without it an offline boot rebuilt the default
    // stage layout while the same collection online restored the one the person had arranged — the
    // downloaded copy quietly being the worse one.
    layout: collection?.layout ?? null,
    // Its logical key, so the library page can read the BYTES back rather than re-request the URL.
    coverKey: collectionCover?.identity ?? null,
    // Assets that belong to the collection itself rather than to any one of its entries. A second
    // list rather than a fake entry, because an entry means "a stage that plays"; `manifestAssets`
    // walks both.
    assets: [...(collectionCover ? [collectionCover] : []), ...sky.assets, ...defaults.assets],
    /** Which sky this download holds, as the catalogue named it when it ran. */
    skyboxId: sky.skyboxId,
    /**
     * The account's fallback orbiter and world, pinned to the versions that were stored. What a file
     * imported from this machine plays with, and what any fallback resolves to — see
     * `offlineDefaultPair.js`. Null when they could not be read.
     */
    defaultPair: defaults.pair,
    /** Retryable: without the stored pair a local/fallback voice is not fully downloaded. */
    defaultPairUnresolved: defaults.unresolved,
    // Where this reopens. Rebuilt from an allowlist rather than copied from the address the
    // download was started at — see `offlineEntryUrl.js` for what is dropped and why a room or a
    // host parameter stored here would boot into something that cannot exist.
    entryUrl: buildOfflineEntryUrl(
      collection.collectionId,
      sourceUrl ?? (typeof window !== 'undefined' ? window.location?.href : null),
    ),
    entries,
    releaseKeys: [...releaseRecords, ...defaults.releaseRecords].map((record) => record.key),
    releaseRecords: [...releaseRecords, ...defaults.releaseRecords],
    skippedTrackIds: skipped,
    /** Tracks whose hydration failed this run. Worth retrying, unlike `skippedTrackIds`. */
    failedTrackIds: failed,
    /** Roster data retained so Resume can retry a transient resolution without a live collection read. */
    failedEntries,
  };
}

/** Every distinct asset a manifest needs, de-duplicated by key (two tracks can share a texture). */
/**
 * The collection's own artwork as an asset, or null when there is none. Exported so a download made
 * before covers were kept can be given one later without re-running the whole download.
 * @param {string|null|undefined} coverUrl
 */
export function collectionCoverAsset(coverUrl) {
  const url = normalize(coverUrl);
  if (!url) return null;
  return optional(asset(collectionCoverKey(url), url, 'collection-cover'));
}

/**
 * What quality a stored collection was downloaded at.
 *
 * The COLLECTION's answer, not a tally of its tracks: a lossless download is lossless, and a track
 * that has no lossless variant simply took the other one. Reporting that as a third state made every
 * row arithmetic ("lossless, 40 of 45") for a distinction nobody acts on.
 *
 * Falls back to reading the variant out of an entry's audio key, which is how a collection
 * downloaded before quality existed answers without a migration — those keys have always spelled
 * `:variant:compressed:`.
 */
export function manifestQuality(manifest) {
  if (manifest?.requestedQuality) return normalizeOfflineQuality(manifest.requestedQuality);
  const identity = manifest?.entries?.[0]?.audio?.[0];
  return identity && String(identity).includes(`:variant:${OFFLINE_QUALITY.LOSSLESS}:`)
    ? OFFLINE_QUALITY.LOSSLESS
    : OFFLINE_QUALITY.COMPRESSED;
}

export function manifestAssets(manifest) {
  const byKey = new Map();
  // Collection-level assets first — the cover belongs to the collection, not to any entry.
  for (const item of manifest?.assets || []) {
    if (item?.key && !byKey.has(item.key)) byKey.set(item.key, item);
  }
  for (const entry of manifest?.entries || []) {
    for (const item of entry?.assets || []) {
      if (item?.key && !byKey.has(item.key)) byKey.set(item.key, item);
    }
  }
  return [...byKey.values()];
}
